/**
 * The draft, and the feasibility guard that keeps it finishable.
 *
 * The user spins, is shown a real squad, and picks one player. This happens in
 * two sequential phases, which are deliberately different kinds of choice:
 *
 *   1. STARTING XI -- eleven players, each assigned to one batting slot inside
 *      their eligible range. Constrained, ordered, and guarded.
 *   2. IMPACT SUBS -- two players, Indians only, any role, any season, and
 *      *no batting order at all*. It is a pool, not a line-up.
 *
 * The phases do not interleave. Nothing can be sent to the bench until the XI
 * is complete, which is what lets the solver below ignore the bench entirely.
 *
 * ## Why the XI needs a solver
 *
 * Its rules interact badly:
 *
 *   - 11 players, each assigned to one batting slot inside their eligible range
 *   - at most 4 overseas players
 *   - at least one wicketkeeper
 *   - at least 5 bowling options (6 recommended -- 5 x 4 overs is exactly 20
 *     and leaves the innings no slack at all)
 *   - no player twice, even from a different season
 *
 * A greedy draft dead-ends. Take four overseas players by slot 6 and every
 * overseas keeper afterwards is unpickable; fill slots 1-6 and a player whose
 * range is 1-6 has nowhere to go; reach the eleventh pick with no keeper and
 * only non-keepers on offer and the team is simply illegal.
 *
 * So before any pick is offered, we ask: if the user takes this player into
 * this slot, does at least one legal completion still exist? Picks that would
 * strand the draft are shown disabled, with the reason.
 *
 * ## Why the bench does not
 *
 * Impact subs take no slot and carry no positional constraint, so the only way
 * the XI phase can strand the bench is by exhausting the Indians -- which the
 * search accounts for with a single reserve count, not a second search. Phase 2
 * itself is then a flat eligibility filter with no backtracking in it.
 *
 * ## What "the pool" means
 *
 * Spins are random, so the user cannot plan around what is coming. Feasibility
 * is therefore judged against every remaining player in the selected year
 * range, not against the squad currently on screen -- any of them could still
 * be offered.
 */

import type { Player } from './types.ts';

/**
 * A player as the draft sees them: the simulation's view plus the batting
 * range, which only the draft cares about.
 */
export interface DraftPlayer extends Player {
  /** Earliest batting slot this player is eligible for, 1-based. */
  readonly posMin: number;
  /** Latest batting slot this player is eligible for, 1-based. */
  readonly posMax: number;
}

export interface DraftRules {
  readonly xiSize: number;
  readonly maxOverseas: number;
  readonly minKeepers: number;
  /** Hard minimum. Below this the innings cannot be bowled. */
  readonly minBowlers: number;
  /** Advisory: five bowlers is exactly 20 overs with no margin. */
  readonly recommendedBowlers: number;
  readonly impactSubs: number;
  readonly impactMustBeIndian: boolean;
}

export const DEFAULT_RULES: DraftRules = {
  xiSize: 11,
  maxOverseas: 4,
  minKeepers: 1,
  minBowlers: 5,
  recommendedBowlers: 6,
  impactSubs: 2,
  impactMustBeIndian: true,
};

/** Where a pick is going. */
export type PickTarget = { readonly kind: 'XI'; readonly slot: number } | { readonly kind: 'IMPACT' };

/**
 * Which phase the draft is in. The UI reads this to decide whether to show
 * batting slots at all -- during IMPACT there is no batting order to show.
 */
export type DraftPhase = 'XI' | 'IMPACT' | 'DONE';

export interface DraftState {
  /** Batting slot (1-based) to the player filling it. */
  readonly xi: ReadonlyMap<number, DraftPlayer>;
  readonly impactSubs: readonly DraftPlayer[];
  /** Identities already used, so the same player cannot be drafted twice. */
  readonly usedIds: ReadonlySet<string>;
  readonly rules: DraftRules;
}

export function createDraft(rules: DraftRules = DEFAULT_RULES): DraftState {
  return { xi: new Map(), impactSubs: [], usedIds: new Set(), rules };
}

/** The XI is filled first, then the bench. They never interleave. */
export function phaseOf(state: DraftState): DraftPhase {
  if (state.xi.size < state.rules.xiSize) return 'XI';
  if (state.impactSubs.length < state.rules.impactSubs) return 'IMPACT';
  return 'DONE';
}

/**
 * Identity for the no-duplicates rule. Two season-versions of the same player
 * are the same person, so this must be the name and not the card id.
 */
export const identityOf = (p: Player): string => p.id.split('|')[0];

const isOverseas = (p: Player): boolean => !p.isIndian;
const canBowl = (p: Player): boolean => p.bowlOvers >= 1;

// ---------------------------------------------------------------------------
// Feasibility
// ---------------------------------------------------------------------------

interface Need {
  /** Open batting slots, ascending. */
  readonly slots: readonly number[];
  readonly overseasUsed: number;
  readonly keepers: number;
  readonly bowlers: number;
  /**
   * Bench places still to fill. The XI search never *assigns* these -- it only
   * makes sure it does not consume so many Indians that they cannot be filled
   * afterwards.
   */
  readonly impactNeeded: number;
}

function needOf(state: DraftState): Need {
  const slots: number[] = [];
  for (let s = 1; s <= state.rules.xiSize; s++) if (!state.xi.has(s)) slots.push(s);

  let overseasUsed = 0;
  let keepers = 0;
  let bowlers = 0;
  for (const p of state.xi.values()) {
    if (isOverseas(p)) overseasUsed++;
    if (p.role === 'WK') keepers++;
    if (canBowl(p)) bowlers++;
  }

  return {
    slots,
    overseasUsed,
    keepers,
    bowlers,
    impactNeeded: state.rules.impactSubs - state.impactSubs.length,
  };
}

/**
 * Players are interchangeable for feasibility purposes if they share these
 * traits and the same slot range. Collapsing the pool onto this signature turns
 * a search over hundreds of players into one over a handful of kinds, which is
 * what makes the guard fast enough to run before every spin.
 */
const signatureOf = (p: DraftPlayer): string =>
  `${p.isIndian ? 'I' : 'O'}${p.role === 'WK' ? 'K' : '-'}${canBowl(p) ? 'B' : '-'}:${p.posMin}-${p.posMax}`;

interface Kind {
  readonly indian: boolean;
  readonly keeper: boolean;
  readonly bowler: boolean;
  readonly posMin: number;
  readonly posMax: number;
  /** How many distinct players of this kind remain available. */
  count: number;
}

/**
 * The collapsed pool, plus the way back from a person to the kind they were
 * counted into.
 *
 * That second map is not a convenience. One person can appear in several
 * seasons with genuinely different signatures -- Ishan Kishan bats 6-8 as a
 * non-keeper in 2016, 1-7 in 2017 and 2-4 as the keeper in 2018 -- so a card
 * cannot be found in the index by its own signature. Looking one up that way
 * finds a different player's kind, or nothing at all.
 */
interface PoolIndex {
  readonly kinds: Kind[];
  /** Identity -> the Kind its count lives in. */
  readonly byIdentity: Map<string, Kind>;
}

function kindsOf(pool: readonly DraftPlayer[], used: ReadonlySet<string>): PoolIndex {
  const byKind = new Map<string, Kind>();
  const byIdentity = new Map<string, Kind>();

  for (const p of pool) {
    const identity = identityOf(p);
    if (used.has(identity)) continue;
    // The same person appearing in several seasons is one draftable player, so
    // counting each season-version separately would overstate availability.
    //
    // Which season-version gets counted is whichever is seen first, and that is
    // deliberately pessimistic: the pool is judged as if each person could only
    // be offered in that one version, when in reality any of their seasons
    // could come up. Understating the pool over-restricts, which stops picks
    // being offered that cannot be completed -- the safe direction. Overstating
    // it would strand drafts.
    if (byIdentity.has(identity)) continue;

    const key = signatureOf(p);
    let kind = byKind.get(key);
    if (kind) kind.count++;
    else {
      kind = {
        indian: p.isIndian,
        keeper: p.role === 'WK',
        bowler: canBowl(p),
        posMin: p.posMin,
        posMax: p.posMax,
        count: 1,
      };
      byKind.set(key, kind);
    }
    byIdentity.set(identity, kind);
  }
  return { kinds: [...byKind.values()], byIdentity };
}

/**
 * Can the remaining batting slots be filled legally, without starving the
 * bench of the Indians it will need afterwards?
 *
 * Backtracking over slots, choosing a *kind* rather than an individual player,
 * with the most constrained slot handled first and failed states memoised.
 *
 * Those two together keep the search tiny: measured over 63,065 feasibility
 * checks across full-range, single-season and three-season pools, the mean was
 * 7.9 nodes and the worst case 203. The budget below is a safety net at ~250x
 * the observed worst case and has never been reached, so in practice the guard
 * is exact rather than conservative -- it never disables a pick that a legal
 * completion actually exists for.
 *
 * If the budget ever were exhausted the answer is no, which over-restricts but
 * still never strands anyone. That is the safe direction to fail in.
 */
function canComplete(need: Need, kinds: Kind[], rules: DraftRules): boolean {
  const totalIndiansAvailable = kinds.reduce((n, k) => n + (k.indian ? k.count : 0), 0);

  // Order slots by how few kinds can fill them: the classic most-constrained
  // -first heuristic, which is what keeps the search shallow.
  const fillableBy = (slot: number): number =>
    kinds.reduce((n, k) => n + (k.posMin <= slot && slot <= k.posMax ? k.count : 0), 0);
  const slots = [...need.slots].sort((a, b) => fillableBy(a) - fillableBy(b));

  let budget = 50_000;
  const seen = new Set<string>();

  const search = (index: number, overseas: number, keepers: number, bowlers: number, indiansUsed: number): boolean => {
    if (--budget < 0) return false;

    const remaining = slots.length - index;

    // Prune: requirements that can no longer be met.
    if (overseas > rules.maxOverseas) return false;
    if (keepers < rules.minKeepers && remaining < rules.minKeepers - keepers) return false;
    if (bowlers < rules.minBowlers && remaining < rules.minBowlers - bowlers) return false;

    if (index === slots.length) {
      if (keepers < rules.minKeepers || bowlers < rules.minBowlers) return false;
      // The bench is picked later, from whatever this XI leaves behind. It
      // needs Indians and nothing else, so one count is the whole check.
      const indiansLeft = totalIndiansAvailable - indiansUsed;
      return !rules.impactMustBeIndian || indiansLeft >= need.impactNeeded;
    }

    const memoKey = `${index}|${overseas}|${keepers}|${bowlers}`;
    if (seen.has(memoKey)) return false;
    seen.add(memoKey);

    const slot = slots[index];

    // Try each kind that can fill this slot. Prefer kinds that satisfy an
    // outstanding requirement, so a legal completion is found early.
    const candidates = kinds
      .filter((k) => k.count > 0 && k.posMin <= slot && slot <= k.posMax)
      .sort((a, b) => {
        const score = (k: Kind): number =>
          (keepers < rules.minKeepers && k.keeper ? 2 : 0) +
          (bowlers < rules.minBowlers && k.bowler ? 1 : 0) +
          (k.indian ? 0.5 : 0);
        return score(b) - score(a);
      });

    for (const k of candidates) {
      k.count--;
      const ok = search(
        index + 1,
        overseas + (k.indian ? 0 : 1),
        keepers + (k.keeper ? 1 : 0),
        bowlers + (k.bowler ? 1 : 0),
        indiansUsed + (k.indian ? 1 : 0),
      );
      k.count++;
      if (ok) return true;
    }
    return false;
  };

  return search(0, need.overseasUsed, need.keepers, need.bowlers, 0);
}

// ---------------------------------------------------------------------------
// Offers
// ---------------------------------------------------------------------------

export interface SlotOption {
  readonly slot: number;
  readonly legal: boolean;
  /** Set when the slot is not legal, for the UI to explain the block. */
  readonly reason?: string;
}

export interface Offer {
  readonly player: DraftPlayer;
  /**
   * Slots this player may be assigned to right now. Always empty outside the
   * XI phase -- impact subs have no batting order.
   */
  readonly legalSlots: readonly number[];
  /** True when the player may go on the impact bench. Only ever set in the IMPACT phase. */
  readonly canBeImpact: boolean;
  /** Set when the player is entirely unpickable. */
  readonly blockedReason?: string;
  /** Per-slot findings, for the UI to explain a partially blocked player. Empty outside the XI phase. */
  readonly slotDetail: readonly SlotOption[];
}

/**
 * Evaluates a spun squad against the current draft state.
 *
 * Every player is reported with what they may legally do right now and, when
 * they can do nothing, why. The reason matters: a disabled card with no
 * explanation reads as a bug.
 *
 * What "legally" means depends on the phase, and the two are not variations on
 * one rule set -- during IMPACT there are no slots, no positional range and no
 * search, just an eligibility test.
 */
export function evaluateSquad(
  state: DraftState,
  squad: readonly DraftPlayer[],
  pool: readonly DraftPlayer[],
  rules: DraftRules = state.rules,
): Offer[] {
  const phase = phaseOf(state);
  const need = needOf(state);

  // Build the kind index ONCE for the whole squad.
  //
  // Rebuilding it per candidate slot walked the entire pool every time, making
  // this O(players x slots x pool). Over the full 2008-2026 range that is
  // 15 x 11 x 2,490 per spin -- hundreds of thousands of operations before the
  // search even starts, which is far too slow to run on a phone between taps.
  // Taking a player is just a decrement on their kind, so the index is built
  // once and mutated in place around each hypothetical.
  const { kinds, byIdentity } = kindsOf(pool, state.usedIds);

  const indiansAvailable = kinds.reduce((n, k) => n + (k.indian ? k.count : 0), 0);

  /** Runs a feasibility check as if `player` had been taken into `slot`. */
  const feasibleAfter = (player: DraftPlayer, slot: number): boolean => {
    // Look the person up by identity, never by the signature of the card on
    // screen. A player's signature changes between seasons -- Rohit Sharma bats
    // 1-4 in 2016 and 1-5 in 2018 -- and the index holds only one of them, so a
    // signature lookup finds someone else's kind or nothing, and reports a
    // perfectly legal pick as impossible.
    const kind = byIdentity.get(identityOf(player));
    // The player must be in the pool; if not, treat them as unavailable rather
    // than silently reasoning about a squad they are not part of.
    if (!kind || kind.count <= 0) return false;

    kind.count--;
    const after = pickInto(state, player, { kind: 'XI', slot });
    // The player being taken contributes their real attributes through
    // needOf(after); only the remaining pool is approximated by kinds.
    const ok = canComplete(needOf(after), kinds, rules);
    kind.count++;
    return ok;
  };

  const blocked = (player: DraftPlayer, blockedReason: string): Offer => ({
    player,
    legalSlots: [],
    canBeImpact: false,
    slotDetail: [],
    blockedReason,
  });

  return squad.map((player) => {
    if (state.usedIds.has(identityOf(player))) return blocked(player, 'Already in your squad');
    if (phase === 'DONE') return blocked(player, 'Your squad is complete');

    // ---- Phase 2: the bench. No slots, no order, no search. ----------------
    if (phase === 'IMPACT') {
      if (rules.impactMustBeIndian && !player.isIndian) {
        return blocked(player, 'Impact subs must be Indian');
      }
      // Only reachable with a pathologically small pool; checked rather than
      // assumed, because "always true in practice" is how drafts get stranded.
      if (indiansAvailable < need.impactNeeded) {
        return blocked(player, 'Not enough Indian players left to fill the bench');
      }
      return { player, legalSlots: [], canBeImpact: true, slotDetail: [] };
    }

    // ---- Phase 1: the XI. Slot assignment, guarded. ------------------------
    const slotDetail: SlotOption[] = [];
    const legalSlots: number[] = [];

    for (const slot of need.slots) {
      if (slot < player.posMin || slot > player.posMax) {
        slotDetail.push({ slot, legal: false, reason: `Bats ${player.posMin}-${player.posMax}` });
        continue;
      }
      if (isOverseas(player) && need.overseasUsed >= rules.maxOverseas) {
        slotDetail.push({ slot, legal: false, reason: `Would exceed ${rules.maxOverseas} overseas players` });
        continue;
      }

      if (!feasibleAfter(player, slot)) {
        slotDetail.push({ slot, legal: false, reason: 'No legal team could be completed from here' });
        continue;
      }

      slotDetail.push({ slot, legal: true });
      legalSlots.push(slot);
    }

    let blockedReason: string | undefined;
    if (legalSlots.length === 0) {
      // Prefer the reason the user can act on. "No free position in 9-10" is
      // true but useless when the real obstacle is the overseas cap, so read
      // the per-slot findings in order of how informative they are rather than
      // guessing from the state.
      const bySlot = (prefix: string): string | undefined =>
        slotDetail.find((s) => s.reason?.startsWith(prefix))?.reason;

      if (isOverseas(player) && need.overseasUsed >= rules.maxOverseas) {
        blockedReason = `Would exceed ${rules.maxOverseas} overseas players`;
      } else {
        blockedReason =
          bySlot('No legal team') ?? `No free position in ${player.posMin}–${player.posMax}`;
      }
    }

    return { player, legalSlots, canBeImpact: false, slotDetail, blockedReason };
  });
}

/**
 * Applies a pick. Does not validate -- call evaluateSquad first.
 *
 * An IMPACT target before the XI is complete is not rejected here, but it is
 * never offered either: evaluateSquad will not set `canBeImpact` during the XI
 * phase, and `validate` catches the resulting squad.
 */
export function pickInto(state: DraftState, player: DraftPlayer, target: PickTarget): DraftState {
  const usedIds = new Set(state.usedIds);
  usedIds.add(identityOf(player));

  if (target.kind === 'IMPACT') {
    return { ...state, impactSubs: [...state.impactSubs, player], usedIds };
  }
  const xi = new Map(state.xi);
  xi.set(target.slot, player);
  return { ...state, xi, usedIds };
}

export function isComplete(state: DraftState): boolean {
  return (
    state.xi.size === state.rules.xiSize && state.impactSubs.length === state.rules.impactSubs
  );
}

export interface Validation {
  readonly legal: boolean;
  readonly errors: readonly string[];
  readonly warnings: readonly string[];
}

/** Final check on a finished squad. A completed draft must always pass this. */
export function validate(state: DraftState): Validation {
  const { rules } = state;
  const errors: string[] = [];
  const warnings: string[] = [];
  const xi = [...state.xi.values()];

  if (state.xi.size !== rules.xiSize) errors.push(`XI has ${state.xi.size} of ${rules.xiSize} players`);
  if (state.impactSubs.length !== rules.impactSubs) {
    errors.push(`${state.impactSubs.length} of ${rules.impactSubs} impact subs chosen`);
  }

  const overseas = xi.filter(isOverseas).length;
  if (overseas > rules.maxOverseas) errors.push(`${overseas} overseas players, maximum is ${rules.maxOverseas}`);

  const keepers = xi.filter((p) => p.role === 'WK').length;
  if (keepers < rules.minKeepers) errors.push('No wicketkeeper');

  const bowlers = xi.filter(canBowl).length;
  if (bowlers < rules.minBowlers) errors.push(`Only ${bowlers} bowling options, need ${rules.minBowlers}`);
  else if (bowlers < rules.recommendedBowlers) {
    warnings.push(
      `${bowlers} bowling options. Five bowlers is exactly 20 overs with no margin -- an injury or an impact sub leaves the innings unbowlable.`,
    );
  }

  if (rules.impactMustBeIndian && state.impactSubs.some((p) => !p.isIndian)) {
    errors.push('Impact subs must be Indian players');
  }

  const identities = [...xi, ...state.impactSubs].map(identityOf);
  if (new Set(identities).size !== identities.length) errors.push('The same player is in the squad twice');

  for (const [slot, p] of state.xi) {
    if (slot < p.posMin || slot > p.posMax) {
      errors.push(`${p.name} is at ${slot} but bats ${p.posMin}-${p.posMax}`);
    }
  }

  return { legal: errors.length === 0, errors, warnings };
}
